import { Injectable, Injector, inject } from '@angular/core';
import { Observable, map, of, take } from 'rxjs';

import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import {
  DiscardChangesDialogComponent,
  DiscardChangesDialogData,
} from './discard-changes-dialog.component';

let nextDiscardChangesDialogId = 0;

export interface DiscardChangesDialogOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface DiscardChangesDialogHandle {
  readonly result$: Observable<boolean>;
  close(result?: boolean): void;
}

@Injectable({ providedIn: 'root' })
export class DiscardChangesDialogService {
  private readonly injector = inject(Injector);

  open(options: DiscardChangesDialogOptions): DiscardChangesDialogHandle {
    const dialogService = this.injector.get(DialogService);
    const titleId = `discard-changes-dialog-${nextDiscardChangesDialogId++}-title`;
    const data: DiscardChangesDialogData = {
      title: options.title ?? 'Porzucić zmiany?',
      message: options.message,
      confirmLabel: options.confirmLabel ?? 'Odrzuć zmiany',
      cancelLabel: options.cancelLabel ?? 'Wróć do edycji',
    };

    const dialogRef = dialogService.open(DiscardChangesDialogComponent, {
      data,
      header: data.title,
      width: '28rem',
      style: { maxWidth: 'calc(100vw - 2rem)' },
      contentStyle: { padding: '0', overflow: 'hidden' },
      modal: true,
      dismissableMask: true,
      closeOnEscape: true,
      closable: false,
      ariaLabelledBy: titleId,
      draggable: false,
      resizable: false,
      baseZIndex: 1200,
      styleClass: 'app-modal--form app-discard-changes-dialog',
    });

    if (!dialogRef) {
      return {
        result$: of(false),
        close: () => {},
      };
    }

    let closed = false;
    const result$ = dialogRef.onClose.pipe(
      take(1),
      map((result) => result === true)
    );

    result$.subscribe(() => {
      closed = true;
    });

    return {
      result$,
      close: (result = false) => {
        if (closed) {
          return;
        }

        closed = true;
        (dialogRef as unknown as DynamicDialogRef<boolean>).close(result);
      },
    };
  }

  confirm(options: DiscardChangesDialogOptions): Observable<boolean> {
    return this.open(options).result$;
  }
}
